import type {
  ResourceLedger,
  ResourceRegistration,
  ResourceReleaseReason,
  ResourceReleaseResult,
} from "./resourceLedger.ts";

export interface OwnedResourceLease {
  readonly registration: ResourceRegistration;
  release(reason: ResourceReleaseReason): ResourceReleaseResult;
}

export interface ResourceOwnership {
  acquire(registration: ResourceRegistration, dispose?: () => void): OwnedResourceLease;
}

export function createResourceOwnership(input: {
  readonly ledger: ResourceLedger;
}): ResourceOwnership {
  let nextGeneration = 0;

  return {
    acquire(registration, dispose = () => undefined) {
      nextGeneration += 1;
      const recordedRegistration: ResourceRegistration = {
        ...registration,
        owner: { ...registration.owner },
        resource: {
          ...registration.resource,
          identity: `${registration.resource.identity}/${nextGeneration}`,
        },
      };
      const registrationResult = input.ledger.register(recordedRegistration);
      if (registrationResult.kind !== "registered") {
        throw new Error(
          `Resource identity '${recordedRegistration.resource.identity}' was already registered.`,
        );
      }

      let releaseResult: ResourceReleaseResult | null = null;
      return {
        registration: recordedRegistration,
        release(reason) {
          if (releaseResult !== null) return releaseResult;
          dispose();
          releaseResult = input.ledger.release({
            owner: recordedRegistration.owner,
            resource: recordedRegistration.resource,
            reason,
          });
          return releaseResult;
        },
      };
    },
  };
}
